import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Loader2,
  Save,
  FileText,
  Plus,
  X,
  Percent,
  CalendarClock,
  Building2,
  ChevronRight,
  Mail,
} from "lucide-react";
import { useBillingConfig, useUpdateBillingConfig } from "../api/billing";
import { SmtpEmisoresTable } from "../components/SmtpEmisoresTable";

export function FacturacionConfigPage() {
  const { data, isLoading } = useBillingConfig();
  const update = useUpdateBillingConfig();

  const [tipos, setTipos] = useState<number[]>([]);
  const [nuevoTipo, setNuevoTipo] = useState("");
  const [ivaDefecto, setIvaDefecto] = useState<number>(21);
  const [vencimiento, setVencimiento] = useState<number>(30);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setTipos(data.iva_tipos ?? []);
    setIvaDefecto(data.iva_defecto ?? 21);
    setVencimiento(data.vencimiento_dias ?? 30);
  }, [data]);

  function addTipo(e: React.FormEvent) {
    e.preventDefault();
    const v = Number(nuevoTipo);
    if (!Number.isFinite(v) || v < 0 || v > 100) return;
    if (tipos.includes(v)) {
      setNuevoTipo("");
      return;
    }
    setTipos((t) => [...t, v].sort((a, b) => b - a));
    setNuevoTipo("");
  }
  function removeTipo(v: number) {
    setTipos((t) => t.filter((x) => x !== v));
    if (ivaDefecto === v) setIvaDefecto(tipos.find((x) => x !== v) ?? 0);
  }

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(false);
    if (tipos.length === 0) {
      setError("Define al menos un tipo de IVA.");
      return;
    }
    const def = tipos.includes(ivaDefecto) ? ivaDefecto : tipos[0];
    try {
      await update.mutateAsync({
        iva_tipos: tipos,
        iva_defecto: def,
        vencimiento_dias: vencimiento,
      });
      setIvaDefecto(def);
      setOk(true);
      setTimeout(() => setOk(false), 2500);
    } catch {
      setError("No se pudieron guardar los cambios.");
    }
  }

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Cargando…
      </div>
    );
  }

  return (
    <div className="p-8 h-full flex flex-col gap-6 min-h-0 overflow-auto">
      <header>
        <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
          <FileText size={28} className="text-brand-700" /> Configuración · Facturación
        </h1>
        <p className="text-slate-500 mt-1">
          Tipos de IVA, valores por defecto y vencimiento de las facturas.
        </p>
      </header>

      <form onSubmit={guardar} className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-6 flex flex-col gap-6 max-w-2xl">
        {error && (
          <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">{error}</div>
        )}
        {ok && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 text-sm">
            Cambios guardados ✓
          </div>
        )}

        {/* Tipos de IVA */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Percent size={13} /> Tipos de IVA disponibles
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {tipos.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 text-brand-700 px-3 py-1.5 text-sm font-bold">
                {t}%
                <button type="button" onClick={() => removeTipo(t)} className="hover:text-rose-600">
                  <X size={13} />
                </button>
              </span>
            ))}
            {tipos.length === 0 && <span className="text-sm text-slate-400">Sin tipos definidos.</span>}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={nuevoTipo}
              onChange={(e) => setNuevoTipo(e.target.value)}
              placeholder="Ej: 21"
              className="w-32 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={addTipo}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700"
            >
              <Plus size={15} /> Añadir tipo
            </button>
          </div>
        </div>

        {/* IVA por defecto */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            IVA por defecto en líneas nuevas
          </label>
          <select
            value={ivaDefecto}
            onChange={(e) => setIvaDefecto(Number(e.target.value))}
            className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm max-w-[200px]"
          >
            {tipos.map((t) => (
              <option key={t} value={t}>
                {t}%
              </option>
            ))}
          </select>
        </div>

        {/* Vencimiento */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <CalendarClock size={13} /> Días de vencimiento por defecto
          </label>
          <input
            type="number"
            min={0}
            max={365}
            value={vencimiento}
            onChange={(e) => setVencimiento(Number(e.target.value))}
            className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm max-w-[200px]"
          />
          <p className="mt-1 text-xs text-slate-400">
            Se usa para calcular el vencimiento cuando el cliente no tiene una forma de pago con plazo.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={update.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            <Save size={14} /> Guardar
          </button>
        </div>
      </form>

      {/* Configuración SMTP por emisor */}
      <section className="max-w-4xl">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={20} className="text-brand-700" />
          <h2 className="text-xl font-black text-slate-900">Envío de facturas por email (SMTP)</h2>
        </div>
        <p className="text-slate-500 text-sm mb-4">
          Cada emisor envía sus facturas desde su propio servidor de correo. El botón
          «enviar» de una factura usa el SMTP del emisor al que pertenece. Si un emisor
          no tiene SMTP, se usa el correo global del sistema.
        </p>
        <SmtpEmisoresTable />
      </section>

      {/* Enlace a Emisores (numeración, textos, Verifacti) */}
      <Link
        to="/billing/issuers"
        className="group flex items-center gap-4 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-soft transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md max-w-2xl"
      >
        <div className="w-12 h-12 rounded-2xl grid place-items-center bg-brand-100 text-brand-700">
          <Building2 size={22} />
        </div>
        <div className="flex-1">
          <div className="font-black text-slate-900">Emisores</div>
          <p className="text-sm text-slate-500">
            Numeración por emisor, textos legales, logo y conexión con Verifacti.
          </p>
        </div>
        <ChevronRight size={18} className="text-slate-300 group-hover:text-brand-600 transition" />
      </Link>
    </div>
  );
}
